/**
 * The call script, client side (plan section 8).
 *
 * Its own file rather than more of `siteBrain.ts`, for the reason
 * `siteBrainEmail.ts` is its own file: that one is about running a brain and
 * reading what it found, these two are about reaching a stranger, and that is
 * the half where a mistake leaves the building.
 *
 * TWO RULES TRAVEL WITH THESE TYPES.
 *
 * 1. NO NUMBER GOES TO THE SERVER. There is no call here that names who to ring.
 *    The number only ever comes back FROM the API, decided by the gate that also
 *    checks the do-not-call list. A client that could supply it would move that
 *    check into the browser, where it is one refactor from not running - and the
 *    failure would be invisible, because a call to somebody who asked us to stop
 *    looks exactly like any other call.
 * 2. AN EDIT IS TEXT AND NOTHING ELSE. `ScriptEdit` carries an id and a string.
 *    The evidence ids, the checklist ids, the source disclosure and the package
 *    prices are not editable, because they are what make a claim provable, a
 *    question countable, a disclosure lawful and a price true.
 */
import { api } from "./api";
import { brainKey, type BrainFinding } from "./siteBrain";

/**
 * How lawful this number is.
 *
 * `blocked` is the only value with no script behind it, and unlike the letter's
 * gate that is the ONLY refusal: a live voice call is not what ESS section
 * 103-1 regulates, so a sole trader who cannot lawfully be written to can still
 * be rung - they are simply owed a sentence about where we got the number.
 */
export type BrainCallLegalState =
  | "register_company"
  | "register_personal"
  | "scraped"
  | "no_number"
  | "blocked";

export type BrainCallLegalReason =
  | "register_contact"
  | "register_sole_trader"
  | "register_personal_number"
  | "site_number"
  | "no_number_known"
  | "suppressed";

export interface BrainCallLegal {
  state: BrainCallLegalState;
  reason: BrainCallLegalReason;
  /** E.164, or null. Never filled in by this app. */
  number: string | null;
  /** The number as its source stored it, for an operator who wants to see it. */
  raw: string | null;
  /** True when the opening owes the callee a sentence about where we got this. */
  needsSourceLine: boolean;
}

export interface BrainCallSuppression {
  value: string;
  source: string;
  /** True for a refusal nobody may quietly downgrade. */
  permanent: boolean;
  at: string;
}

export interface BrainCallLegalView extends BrainCallLegal {
  domain: string;
  suppression: BrainCallSuppression | null;
}

/** The parts of a call, in the order they happen. */
export type BrainScriptSectionId = "opening" | "hooks" | "questions" | "objections" | "close";

export interface BrainScriptLine {
  id: string;
  text: string;
  /** The objection, as a customer says it. Only the objections section has one. */
  cue?: string;
  /**
   * How we fix what this line just named. Only the hooks section has one.
   *
   * Its own string rather than more sentences inside `text`, because it answers
   * a different moment: the operator says what is broken, the owner of the page
   * thinks "and then what", and the answer has to be one glance away. Absent on
   * scripts written before the field existed; the box is offered regardless.
   */
  fix?: string;
  findingIds?: string[];
  checklistId?: string;
  /** Server-computed: this line cites a finding the run no longer has. */
  unproven?: boolean;
}

export interface BrainScriptSection {
  id: BrainScriptSectionId;
  lines: BrainScriptLine[];
}

/** One rung of the ladder, priced from the catalogue when the script was written. */
export interface BrainPackage {
  label: string;
  planCode: string;
  monthlyEur: number;
  yearlyMonthlyEur: number | null;
  setupEur: number;
  /** True until muugijuhis section 14.4 is decided. Printed next to the number. */
  proposed: boolean;
  fit: string;
}

/** What the run history proved has been repaired since the last contact. */
export interface BrainScriptProgress {
  /** Defects a later measurement proved gone. Never empty when present. */
  fixed: BrainFinding[];
  /** Measured defects that were there before and still are. */
  stayed: number;
}

export interface BrainCallScript {
  runSeq: number;
  sections: BrainScriptSection[];
  /** Written by the server, not editable here. Rendered under the opening. */
  sourceLine: string | null;
  /**
   * What this company has repaired since we last spoke, or null (R8 history).
   *
   * ⚠ THE FINDINGS IN HERE ARE NOT IN `brain.findings`, and that is the point:
   * they were measured, then measured again and found gone. So they never carry
   * an `unproven` mark and must not be rendered as if they were current defects.
   *
   * Null is the ordinary case. The server only fills it for a company that has
   * already heard from us, and only when the comparison could prove the repair.
   */
  progress?: BrainScriptProgress | null;
  legalState: BrainCallLegalState;
  legalReason: BrainCallLegalReason;
  packages: BrainPackage[];
  findingIds: string[];
  generatedAt: string;
  editedAt?: string;
  model: string;
}

/** What the operator concluded. Not the transcript's own objection list. */
export type BrainCallOutcome =
  | "reached"
  | "gatekeeper"
  | "no_answer"
  | "callback"
  | "meeting"
  | "refused";

/**
 * That a call happened.
 *
 * Read from columns rather than from the script, so a re-run cannot erase it.
 * `callLogId` is null once that call has been erased; the measurement stays.
 */
export interface BrainCallTouch {
  calledAt: string;
  callLogId: string | null;
  outcome: BrainCallOutcome | null;
}

export interface BrainScriptResult {
  ok: boolean;
  script: BrainCallScript;
  legal: BrainCallLegal;
  freshness?: BrainFreshnessLike;
}

/** The freshness shape the letter already defines. Repeated structurally, not imported,
 *  so a change to one gate cannot silently reshape the other. */
export interface BrainFreshnessLike {
  at: string;
  checks: { findingId: string; url: string | null; verdict: "fresh" | "stale" | "unchecked"; detail: string | null }[];
  stale: string[];
  synthesisStale: boolean;
}

/** The refusal bodies, which carry more than a message. */
export interface BrainScriptRefusal {
  error: "no_synthesis" | "blocked" | "stale_evidence" | "pass_failed";
  detail?: string;
  legal: BrainCallLegal;
  freshness?: BrainFreshnessLike;
}

/** One correction: a line id and the text that replaces it. */
export interface ScriptEdit {
  id: string;
  text: string;
}

/**
 * The suffix that aims an edit at a hook's remedy rather than its claim.
 *
 * The server knows the same constant (`script/edits.ts`). Repeated here rather
 * than shared, like the freshness shape above: these two files are an HTTP call
 * apart, and a shared literal would hide that the wire format is the contract.
 */
export const FIX_EDIT_SUFFIX = ":fix";

const key = (domain: string): string => encodeURIComponent(brainKey(domain));

/** May we ring this company, and if not, why not. Safe to ask on card open. */
export const getBrainCallLegal = (domain: string): Promise<BrainCallLegalView> =>
  api.get<BrainCallLegalView>(`/admin/ariregister/brain/${key(domain)}/call-legal`);

/** What to say. 404 means "press the button first". */
export const getBrainScript = (domain: string): Promise<{ domain: string; script: BrainCallScript }> =>
  api.get<{ domain: string; script: BrainCallScript }>(`/admin/ariregister/brain/${key(domain)}/script`);

/**
 * Write the script.
 *
 * `force` is the operator confirming an old verdict or a stale proof, never a
 * default. Even forced, the server drops arguments whose evidence has gone
 * stale: confirming is permission to work from an older run, not permission to
 * open a call on a disproven claim.
 */
export const generateBrainScript = (domain: string, force = false): Promise<BrainScriptResult> =>
  api.post<BrainScriptResult>(`/admin/ariregister/brain/${key(domain)}/script`, { force });

/** Save the operator's corrections. Text only. */
export const saveBrainScriptEdits = (
  domain: string,
  edits: ScriptEdit[],
): Promise<{ ok: boolean; script: BrainCallScript }> =>
  api.patch<{ ok: boolean; script: BrainCallScript }>(`/admin/ariregister/brain/${key(domain)}/script`, { edits });

/** The call was placed. Best effort: never blocks the operator. */
export const markBrainCallPlaced = (
  domain: string,
  callLogId?: string,
): Promise<{ ok: boolean; stamped: boolean }> =>
  api.post<{ ok: boolean; stamped: boolean }>(`/admin/ariregister/brain/${key(domain)}/script/called`, { callLogId });

/** What came of it. Overwritable: correcting a conclusion is not a second call. */
export const recordBrainCallOutcome = (
  domain: string,
  outcome: BrainCallOutcome,
): Promise<{ ok: boolean; touch: BrainCallTouch }> =>
  api.post<{ ok: boolean; touch: BrainCallTouch }>(`/admin/ariregister/brain/${key(domain)}/script/outcome`, { outcome });

/** They said do not ring again. Writes the row the dialer guard already reads. */
export const refuseBrainCall = (
  domain: string,
  note?: string,
): Promise<{ ok: boolean; written: boolean; value: string | null }> =>
  api.post<{ ok: boolean; written: boolean; value: string | null }>(
    `/admin/ariregister/brain/${key(domain)}/refuse-call`,
    { note },
  );
